import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Pricing.css';

const Pricing = () => {
  const [activeFAQ, setActiveFAQ] = useState(null);

  const smsPricing = [
    {
      name: 'Starter',
      price: 999,
      period: 'month',
      sms: '10,000 SMS/month',
      description: 'Small businesses and startups',
      badge: null,
      features: [
        'Transactional and Promotional SMS',
        'Dashboard access with real-time delivery reports',
        'REST API with full documentation',
        'DLT registration support',
        'Sender ID management (up to 3 IDs)',
        'Email support (response within 4 hours)'
      ],
      highlighted: false,
      cta: 'Start Free Trial'
    },
    {
      name: 'Growth',
      price: 2999,
      period: 'month',
      sms: '35,000 SMS/month',
      description: 'Growing businesses with regular campaigns',
      badge: 'MOST POPULAR',
      features: [
        'Transactional, Promotional, and OTP SMS',
        'Priority route access for faster delivery',
        'Dedicated Sender ID (up to 6 IDs)',
        'Advanced analytics with click-through tracking',
        'REST API + SMPP connectivity',
        'Webhook support for real-time delivery notifications',
        'Phone and email support during business hours'
      ],
      highlighted: true,
      cta: 'Get Started'
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      sms: 'Unlimited SMS with SLA',
      description: 'Large enterprises, banks, e-commerce',
      badge: null,
      features: [
        'All features of the Growth plan',
        'Custom-negotiated routes and delivery SLA',
        'Dedicated account manager',
        'White-labelling option for resellers',
        'On-premise deployment option',
        'Priority 24/7 phone support',
        'Custom reporting and API integration support'
      ],
      highlighted: false,
      cta: 'Contact Sales'
    }
  ];

  const ivrPricing = [
    {
      name: 'Basic IVR',
      price: 1499,
      period: 'month',
      includes: '1 IVR number, 500 minutes per month',
      details: [
        '1 dedicated DID number',
        'Up to 3 IVR menu levels',
        'Business hours and after-hours routing',
        'Basic call analytics and reports',
        'Email alerts for missed calls'
      ],
      highlighted: false,
      cta: 'Get Started'
    },
    {
      name: 'Pro IVR',
      price: 3999,
      period: 'month',
      includes: '5 DID numbers, unlimited minutes',
      details: [
        '5 dedicated DID or toll-free numbers',
        'Unlimited call minutes',
        'Multi-level IVR menus (up to 5 levels)',
        'Call recording included — all calls stored for 90 days',
        'Live agent handoff capability',
        'Real-time call dashboard and analytics',
        'REST API and CRM integration',
        'Phone and email support'
      ],
      highlighted: true,
      badge: 'MOST POPULAR',
      cta: 'Get Started'
    },
    {
      name: 'Enterprise IVR',
      price: 'Custom',
      period: '',
      includes: 'Unlimited numbers, cloud or on-premise',
      details: [
        'Unlimited DID and toll-free numbers',
        'Full cloud call centre suite',
        'Custom IVR flow design by our team',
        'CRM and OMS integration',
        'Uptime SLA guarantee in writing',
        'Dedicated support team'
      ],
      highlighted: false,
      cta: 'Contact Sales'
    }
  ];

  const addOns = [
    { name: 'Toll-Free Number (1800 series)', price: 'Rs. 1,000', period: '/month' },
    { name: 'Additional DID Number', price: 'Rs. 500', period: '/month' },
    { name: 'WhatsApp Business API Setup', price: 'Rs. 5,000', period: 'one-time setup fee' },
    { name: 'Stockbroker Software (Sampark, TradeInfo, etc.)', price: 'Custom quote', period: 'based on trading volume' },
    { name: 'Real Estate Tracking Platform', price: 'Custom quote', period: 'based on number of agents' },
    { name: 'Tally SMS Integration Setup', price: 'Rs. 3,000', period: 'one-time setup fee' },
    { name: 'Call Recording Extended Storage (beyond 90 days)', price: 'Rs. 500', period: '/month per additional 3 months' }
  ];

  const faqs = [
    {
      q: 'Is there a free trial?',
      a: 'Yes. We offer a free trial account that lets you send test messages, explore the dashboard, and access the API — without requiring a credit card. Contact our team to activate your trial.'
    },
    {
      q: 'Can I upgrade or downgrade my plan?',
      a: 'Absolutely. You can change your plan at any time. Upgrades take effect immediately. Downgrades apply from the next billing cycle.'
    },
    {
      q: 'Are GST and other taxes included in the listed prices?',
      a: 'The prices listed are exclusive of GST. GST at the applicable rate will be added to your invoice. We provide a proper GST invoice for all payments.'
    },
    {
      q: 'What is DLT and do I need to register?',
      a: 'DLT (Distributed Ledger Technology) is TRAI\'s mandatory registration system for all commercial SMS senders in India. If you want to send any SMS to Indian mobile numbers, your Sender ID and message templates must be registered on the DLT portal. We help all our clients through this process as part of our onboarding.'
    },
    {
      q: 'Do you offer annual billing discounts?',
      a: 'Yes. Clients who pay annually receive a discount of up to 20% compared to the monthly rate. Contact our sales team for the current annual billing offer.'
    },
    {
      q: 'What payment methods do you accept?',
      a: 'We accept NEFT/RTGS bank transfer, UPI, credit card, debit card, and cheque for enterprise clients. Invoices are raised on the 1st of each month.'
    }
  ];

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  const annualSavings = (monthlyPrice) => {
    if (typeof monthlyPrice === 'number') {
      return Math.round(monthlyPrice * 12 * 0.2);
    }
    return null;
  };

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <div className="container">
          <div className="pricing-hero-content">
            <h1>Simple, Transparent Pricing. No Surprises.</h1>
            <p className="hero-subtitle">
              We believe pricing should be as straightforward as the service itself. No hidden fees. No minimum lock-in commitments that trap you. No per-user charges that add up without you realising. Just honest, clear plans that let you start small and scale as your business grows.
            </p>
          </div>
        </div>
      </section>

      <section className="sms-pricing-section">
        <div className="container">
          <h2 className="section-title">SMS Pricing</h2>
          <p className="section-subtitle">Choose Your SMS Plan</p>
          <p className="section-desc">All plans include dashboard access, REST API, real-time delivery reports, DLT support, and email support. Upgrade or downgrade at any time.</p>
          <div className="pricing-cards-container">
            {smsPricing.map((plan, idx) => (
              <div
                className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}
                key={idx}
              >
                {plan.badge && <div className="badge">{plan.badge}</div>}
                <div className="card-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="card-price">
                  {typeof plan.price === 'number' ? (
                    <>
                      <span className="currency">Rs. </span>
                      <span className="amount">{plan.price.toLocaleString('en-IN')}</span>
                    </>
                  ) : (
                    <span className="custom-price">{plan.price}</span>
                  )}
                  {plan.period && <span className="period">/{plan.period}</span>}
                </div>

                <div className="card-sms-limit">
                  <strong>{plan.sms}</strong>
                </div>

                <button className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-outline'}`}>
                  {plan.cta}
                </button>

                <div className="divider"></div>

                <div className="features-list">
                  {plan.features.map((feature, i) => (
                    <div className="feature" key={i}>
                      <span className="check">✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ivr-pricing-section">
        <div className="container">
          <h2 className="section-title">IVR & Voice Plans</h2>
          <div className="ivr-pricing-wrapper">
            {ivrPricing.map((plan, idx) => (
              <div className={`ivr-card ${plan.highlighted ? 'highlighted' : ''}`} key={idx}>
                <h4 className="ivr-plan-name">{plan.name}</h4>
                <div className="ivr-price">
                  {typeof plan.price === 'number' ? (
                    <>
                      <span className="currency">Rs. </span>
                      <span className="amount">{plan.price.toLocaleString('en-IN')}</span>
                    </>
                  ) : (
                    <span className="custom-price">{plan.price}</span>
                  )}
                  {plan.period && <span className="period">/{plan.period}</span>}
                </div>
                {plan.includes && <div className="ivr-includes"><strong>Includes: {plan.includes}</strong></div>}
                <ul className="ivr-details">
                  {plan.details.map((detail, i) => (
                    <li key={i}>
                      <span className="check">✓</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
                <button className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-outline'} btn-full`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="addons-section">
        <div className="container">
          <h2 className="section-title">Optional Add-Ons (Available with Any Plan)</h2>
          <div className="addons-grid">
            {addOns.map((addon, idx) => (
              <div className="addon-item" key={idx}>
                <h4 className="addon-name">{addon.name}</h4>
                <div className="addon-price">
                  <span className="price">{addon.price}</span>
                  <span className="period">{addon.period}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="pricing-faq-container">
            {faqs.map((faq, idx) => (
              <div
                className={`pricing-faq-item ${activeFAQ === idx ? 'active' : ''}`}
                key={idx}
              >
                <button className="faq-question" onClick={() => toggleFAQ(idx)}>
                  <span>{faq.q}</span>
                  <span className="faq-icon">{activeFAQ === idx ? '−' : '+'}</span>
                </button>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-cta-section">
        <div className="container">
          <div className="pricing-cta-content">
            <h2>Not Sure Which Plan is Right for You?</h2>
            <p>Our team will ask you a few simple questions about your business and recommend the plan that gives you the best value. No pressure, no upselling — just honest advice.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">Talk to Sales</Link>
              <a href="tel:+918800418585" className="btn btn-secondary">+91 8800418585</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
